import React, { useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * InstagramShareConfirmCard — confirm card for
 *   `pending_action.type === "share_to_instagram"`.
 *
 * Mirrors TikTokShareConfirmCard / SnapShareConfirmCard so the user gets
 * ONE consistent confirm flow across all social platforms.
 *
 * Behavior:
 *  - Instagram's Content Publishing API publishes DIRECTLY to the user's
 *    feed (unlike TikTok which sends to drafts). On tap, we hit
 *    `POST /api/instagram/share/photo` with `{photo_id|wallpaper_id, caption}`.
 *  - Backend uses PULL_FROM_URL on aura.wwwwhynot.com's public share routes.
 *  - Requires the connected IG account be Business or Creator.
 *
 * Props
 * -----
 *  - pending: { caption_text, attachment_kind, attachment_id, wallpaper_id, char_count }
 *  - onSent / onCancelled: parent resolution callbacks.
 */
export function InstagramShareConfirmCard({ pending, onSent, onCancelled }) {
  const [text, setText] = useState(pending?.caption_text || "");
  const [posting, setPosting] = useState(false);
  const [status, setStatus] = useState(null); // 'sent' | 'cancelled' | 'error'
  const [errorMsg, setErrorMsg] = useState("");

  const charCount = text.length;
  // Instagram caption hard limit is 2200 chars
  const overLimit = charCount > 2200;

  const attachmentKind = pending?.attachment_kind || (pending?.wallpaper_id ? "wallpaper" : null);
  const attachmentId = pending?.attachment_id || pending?.wallpaper_id || null;

  if (status === "sent") {
    return (
      <div className="tweet-confirm-card sent" data-testid="instagram-confirm-card-sent">
        <span>✓ Posted to your Instagram feed.</span>
      </div>
    );
  }
  if (status === "cancelled") {
    return (
      <div className="tweet-confirm-card cancelled" data-testid="instagram-confirm-card-cancelled">
        ↩ Instagram cancelled — nothing posted.
      </div>
    );
  }

  return (
    <div className="tweet-confirm-card instagram-confirm-card" data-testid="instagram-confirm-card">
      <div className="tweet-confirm-header">
        <span className="tweet-confirm-bird" aria-hidden="true">📷</span>
        <span className="tweet-confirm-label">Post to Instagram feed</span>
        <span className={`tweet-confirm-count ${overLimit ? "over" : ""}`}>
          {charCount}/2200
        </span>
      </div>
      <textarea
        className="tweet-confirm-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        maxLength={2300}
        data-testid="instagram-confirm-text"
        placeholder="Instagram caption — feel free to add line breaks + #hashtags"
      />
      {errorMsg && (
        <div className="tweet-confirm-error" data-testid="instagram-confirm-error">
          {errorMsg}
        </div>
      )}

      <div className="tweet-confirm-actions">
        <button
          className="tweet-confirm-cancel"
          onClick={() => { setStatus("cancelled"); onCancelled?.(); }}
          disabled={posting}
          data-testid="instagram-confirm-cancel"
        >
          Cancel
        </button>
        <button
          className="tweet-confirm-send"
          disabled={posting || overLimit || !attachmentId}
          data-testid="instagram-confirm-send"
          onClick={async () => {
            setPosting(true);
            setErrorMsg("");
            try {
              const payload = attachmentKind === "wallpaper"
                ? { wallpaper_id: attachmentId, caption: text || undefined }
                : { photo_id: attachmentId,    caption: text || undefined };
              await axios.post(`${API}/instagram/share/photo`, payload);
              setStatus("sent");
              onSent?.();
            } catch (e) {
              const detail = e?.response?.data?.detail || "Instagram didn't accept the post — try again?";
              setErrorMsg(detail);
            } finally {
              setPosting(false);
            }
          }}
        >
          {posting ? "Posting…" : "Post to Instagram"}
        </button>
      </div>
    </div>
  );
}

export default InstagramShareConfirmCard;
